import { useState, useEffect, useCallback } from 'react'
import { Check, ExternalLink, Settings, AlertCircle, Loader2, Eye, EyeOff, Trash2 } from 'lucide-react'
import { useTenant } from '@/hooks/useTenant'
import { getAuthToken } from '@/hooks/useAuth'
import { API_BASE } from '@/services/api'

interface Integration {
  id: string
  name: string
  description: string
  icon: string
  status: 'connected' | 'disconnected' | 'coming_soon' | 'loading'
  category: string
  fields?: IntegrationField[]
}

interface IntegrationField {
  name: string
  label: string
  type: 'text' | 'password' | 'url'
  placeholder: string
  required: boolean
  helpText?: string
  suffix?: string
}

const integrationConfigs: Record<string, IntegrationField[]> = {
  openai: [
    {
      name: 'api_key',
      label: 'API Key',
      type: 'password',
      placeholder: 'sk-proj-...',
      required: true,
      helpText: 'Get your API key from platform.openai.com'
    },
    {
      name: 'model',
      label: 'Model (optional)',
      type: 'text',
      placeholder: 'gpt-5-mini',
      required: false,
      helpText: 'Default: gpt-5-mini'
    }
  ],
  gemini: [
    {
      name: 'api_key',
      label: 'API Key',
      type: 'password',
      placeholder: 'AIza...',
      required: true,
      helpText: 'Get your API key from aistudio.google.com'
    },
    {
      name: 'model',
      label: 'Model (optional)',
      type: 'text',
      placeholder: 'gemini-2.0-flash',
      required: false,
      helpText: 'Default: gemini-2.0-flash'
    }
  ],
  grok: [
    {
      name: 'api_key',
      label: 'API Key',
      type: 'password',
      placeholder: 'xai-...',
      required: true,
      helpText: 'Get your API key from x.ai'
    },
    {
      name: 'model',
      label: 'Model (optional)',
      type: 'text',
      placeholder: 'grok-2-latest',
      required: false,
      helpText: 'Default: grok-2-latest'
    }
  ],
  claude: [
    {
      name: 'api_key',
      label: 'API Key',
      type: 'password',
      placeholder: 'sk-ant-...',
      required: true,
      helpText: 'Get your API key from console.anthropic.com'
    },
    {
      name: 'model',
      label: 'Model (optional)',
      type: 'text',
      placeholder: 'claude-opus-4-5',
      required: false,
      helpText: 'Default: claude-opus-4-5'
    }
  ],
  shopify: [
    {
      name: 'shop_url',
      label: 'Store URL',
      type: 'text',
      placeholder: 'your-store',
      required: true,
      suffix: '.myshopify.com'
    },
    {
      name: 'access_token',
      label: 'Access Token',
      type: 'password',
      placeholder: 'shpat_xxxxx',
      required: true,
      helpText: 'Create a private app in your Shopify admin'
    }
  ],
  gmail: [
    {
      name: 'client_id',
      label: 'Client ID',
      type: 'text',
      placeholder: 'xxxxx.apps.googleusercontent.com',
      required: true,
    },
    {
      name: 'client_secret',
      label: 'Client Secret',
      type: 'password',
      placeholder: 'GOCSPX-xxxxx',
      required: true,
    },
    {
      name: 'refresh_token',
      label: 'Refresh Token',
      type: 'password',
      placeholder: '1//xxxxx',
      required: true,
      helpText: 'Obtained after OAuth flow'
    }
  ],
  slack: [
    {
      name: 'webhook_url',
      label: 'Webhook URL',
      type: 'url',
      placeholder: 'https://hooks.slack.com/services/...',
      required: true,
      helpText: 'Create an incoming webhook in Slack'
    }
  ]
}

const defaultIntegrations: Integration[] = [
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'Power your AI agent with GPT-5',
    icon: '🤖',
    status: 'disconnected',
    category: 'Agentic Models',
    fields: integrationConfigs.openai
  },
  {
    id: 'gemini',
    name: 'Gemini',
    description: 'Use Google\'s Gemini AI models',
    icon: '✨',
    status: 'disconnected',
    category: 'Agentic Models',
    fields: integrationConfigs.gemini
  },
  {
    id: 'grok',
    name: 'Grok',
    description: 'Use xAI\'s Grok models',
    icon: '⚡',
    status: 'disconnected',
    category: 'Agentic Models',
    fields: integrationConfigs.grok
  },
  {
    id: 'claude',
    name: 'Claude',
    description: 'Use Anthropic\'s Claude models',
    icon: '🧠',
    status: 'disconnected',
    category: 'Agentic Models',
    fields: integrationConfigs.claude
  },
  {
    id: 'shopify',
    name: 'Shopify',
    description: 'Sync orders, products, and customer data',
    icon: '🛒',
    status: 'disconnected',
    category: 'E-commerce',
    fields: integrationConfigs.shopify
  },
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Process support emails automatically',
    icon: '📧',
    status: 'disconnected',
    category: 'Communication',
    fields: integrationConfigs.gmail
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Get notifications for escalated cases',
    icon: '💬',
    status: 'disconnected',
    category: 'Communication',
    fields: integrationConfigs.slack
  },
]

// Module-level cache — persists across navigation (survives unmount/remount)
let _cachedIntegrations: Integration[] | null = null
let _cacheTimestamp = 0
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

function getCachedIntegrations(): Integration[] | null {
  if (_cachedIntegrations && Date.now() - _cacheTimestamp < CACHE_TTL) {
    return _cachedIntegrations
  }
  return null
}

function setCachedIntegrations(integrations: Integration[]) {
  _cachedIntegrations = integrations
  _cacheTimestamp = Date.now()
}

export default function IntegrationsPage() {
  const { tenantId, isLoading: tenantLoading } = useTenant()
  const cached = getCachedIntegrations()
  const [integrations, setIntegrations] = useState<Integration[]>(cached || defaultIntegrations)
  const [isLoading, setIsLoading] = useState(!cached)
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null)
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({})
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const getHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId || '',
    }
    if (token && token !== 'demo-token') {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }, [tenantId])

  // Load on mount: if cached, skip fetch; otherwise fetch
  useEffect(() => {
    if (tenantId && !tenantLoading) {
      if (!cached) {
        loadIntegrationStatus()
      }
    }
  }, [tenantId, tenantLoading])

  const loadIntegrationStatus = async () => {
    if (!tenantId) return

    try {
      const headers = await getHeaders()
      const response = await fetch(`${API_BASE}/v1/integrations`, { headers })

      if (response.ok) {
        const data = await response.json()
        const connectedIds = data.integrations || []

        const updated = defaultIntegrations.map(int => ({
          ...int,
          status: connectedIds.includes(int.id) ? 'connected' as const :
                  int.status === 'coming_soon' ? 'coming_soon' as const : 'disconnected' as const
        }))

        setIntegrations(updated)
        setCachedIntegrations(updated)
      }
    } catch (err) {
      console.error('Error loading integration status:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const categories = [...new Set(integrations.map(i => i.category))]

  const handleConnect = (integration: Integration) => {
    if (integration.status === 'coming_soon') return
    setSelectedIntegration(integration)
    setFormData({})
    setError(null)
    setSuccess(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedIntegration) return

    setIsSubmitting(true)
    setError(null)

    try {
      const headers = await getHeaders()
      const response = await fetch(`${API_BASE}/v1/integrations/${selectedIntegration.id}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to save integration')
      }

      const result = await response.json()

      // Update local state + cache
      setIntegrations(prev => {
        const updated = prev.map(int =>
          int.id === selectedIntegration.id
            ? { ...int, status: 'connected' as const }
            : int
        )
        setCachedIntegrations(updated)
        return updated
      })

      // Show contextual success message based on integration type
      const contextMessages: Record<string, string> = {
        openai: 'OpenAI connected! AI responses are now enabled.',
        gmail: 'Gmail connected! AI will start processing emails within 60 seconds.',
        shopify: `Shopify connected! ${result.test_details || 'Customer profiles will appear in ticket details.'}`,
        gemini: 'Gemini connected! You can now use Gemini models.',
        grok: 'Grok connected! You can now use Grok models.',
        claude: 'Claude connected! You can now use Claude models.',
        slack: 'Slack connected! You will receive notifications for escalated cases.',
      }
      setSuccess(contextMessages[selectedIntegration.id] || `${selectedIntegration.name} connected successfully!`)
      setTimeout(() => {
        setSelectedIntegration(null)
        setSuccess(null)
      }, 3000)

    } catch (err: any) {
      setError(err.message || 'Failed to connect integration')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDisconnect = async (integration: Integration) => {
    if (!confirm(`Are you sure you want to disconnect ${integration.name}?`)) return

    try {
      const headers = await getHeaders()
      const response = await fetch(`${API_BASE}/v1/integrations/${integration.id}`, {
        method: 'DELETE',
        headers,
      })

      if (response.ok) {
        setIntegrations(prev => {
          const updated = prev.map(int =>
            int.id === integration.id
              ? { ...int, status: 'disconnected' as const }
              : int
          )
          setCachedIntegrations(updated)
          return updated
        })
      }
    } catch (err) {
      console.error('Error disconnecting:', err)
    }
  }

  const toggleShowSecret = (fieldName: string) => {
    setShowSecrets(prev => ({ ...prev, [fieldName]: !prev[fieldName] }))
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
        <p className="text-gray-500 mt-1">Connect your tools and services to power your AI agent</p>
      </div>

      {/* Loading skeleton on first load */}
      {isLoading ? (
        <div className="space-y-6">
          {['Agentic Models', 'E-commerce', 'Communication'].map((cat) => (
            <div key={cat}>
              <div className="h-6 w-40 bg-gray-200 rounded animate-pulse mb-4" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: cat === 'Agentic Models' ? 4 : 2 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gray-100 animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                        <div className="h-3 w-40 bg-gray-100 rounded animate-pulse" />
                      </div>
                    </div>
                    <div className="h-4 w-20 bg-gray-100 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (<>

      {/* Required Banner */}
      {!integrations.find(i => i.id === 'openai' && i.status === 'connected') && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">OpenAI API Key Required</p>
              <p className="text-sm text-yellow-700 mt-1">
                To use the AI chat features, you need to connect your OpenAI account first.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Integrations by category */}
      {categories.map((category) => (
        <div key={category}>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{category}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {integrations
              .filter((i) => i.category === category)
              .map((integration) => (
                <div
                  key={integration.id}
                  className={`bg-white rounded-xl p-6 shadow-sm border ${
                    integration.status === 'connected' ? 'border-green-200' : 'border-gray-100'
                  } ${
                    integration.status !== 'coming_soon' ? 'card-hover cursor-pointer' : 'opacity-75'
                  }`}
                  onClick={() => integration.status !== 'coming_soon' && handleConnect(integration)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                        integration.status === 'connected' ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        {integration.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{integration.name}</h3>
                        <p className="text-sm text-gray-500">{integration.description}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    {integration.status === 'connected' ? (
                      <span className="flex items-center text-sm text-green-600">
                        <Check className="w-4 h-4 mr-1" />
                        Connected
                      </span>
                    ) : integration.status === 'coming_soon' ? (
                      <span className="text-sm text-gray-400">Coming soon</span>
                    ) : (
                      <span className="text-sm text-gray-500">Not connected</span>
                    )}

                    {integration.status === 'connected' ? (
                      <div className="flex space-x-1">
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleConnect(integration); }}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                          <Settings className="w-4 h-4 text-gray-400" />
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleDisconnect(integration); }}
                          className="p-2 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    ) : integration.status !== 'coming_soon' ? (
                      <button className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium rounded-lg transition-colors">
                        Connect
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
          </div>
        </div>
      ))}

      </>)}

      {/* Connection Modal */}
      {selectedIntegration && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-2xl">
                  {selectedIntegration.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">
                    {selectedIntegration.status === 'connected' ? 'Update' : 'Connect'} {selectedIntegration.name}
                  </h3>
                  <p className="text-sm text-gray-500">{selectedIntegration.description}</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm flex items-center">
                  <Check className="w-4 h-4 mr-2" />
                  {success}
                </div>
              )}

              {selectedIntegration.fields?.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <div className="relative flex">
                    <input
                      type={field.type === 'password' && !showSecrets[field.name] ? 'password' : 'text'}
                      placeholder={field.placeholder}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      required={field.required}
                      className={`flex-1 px-4 py-2 border border-gray-300 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${
                        field.suffix ? 'rounded-l-lg border-r-0' : 'rounded-lg'
                      }`}
                    />
                    {field.type === 'password' && (
                      <button
                        type="button"
                        onClick={() => toggleShowSecret(field.name)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                        style={{ right: field.suffix ? '120px' : '8px' }}
                      >
                        {showSecrets[field.name] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                    {field.suffix && (
                      <span className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-r-lg text-gray-500 text-sm">
                        {field.suffix}
                      </span>
                    )}
                  </div>
                  {field.helpText && (
                    <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
                  )}
                </div>
              ))}

              {/* Help links */}
              {selectedIntegration.id === 'openai' && (
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-sm text-yellow-600 hover:text-yellow-700"
                >
                  Get your OpenAI API key
                  <ExternalLink className="w-4 h-4 ml-1" />
                </a>
              )}

              {selectedIntegration.id === 'shopify' && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium">Required permissions:</p>
                      <ul className="mt-1 list-disc list-inside text-yellow-700">
                        <li>read_orders</li>
                        <li>read_products</li>
                        <li>read_customers</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-gray-100 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setSelectedIntegration(null)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {selectedIntegration.status === 'connected' ? 'Update' : 'Connect'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
